import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, CurrencyDollar, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

interface Kpis {
  total_rate_revenue: number;
  total_assessments: number;
  total_arrears: number;
  avg_collection_rate: number;
  financial_year: string;
}

// ── Constants ─────────────────────────────────────────
const COLORS = ["#F59E0B","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1","#14B8A6","#D946EF"];
const CATEGORY_COLORS: Record<string, string> = {
  Residential: "#F59E0B",
  Commercial: "#38BDF8",
  Industrial: "#F472B6",
  "Cultural/Rec": "#34D399",
};

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const CATEGORY_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Categories" },
  { key: "Residential", text: "Residential" },
  { key: "Commercial", text: "Commercial" },
  { key: "Industrial", text: "Industrial" },
  { key: "Cultural/Rec", text: "Cultural/Rec" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtCurrency(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  if (isNaN(n)) return "-";
  if (Math.abs(n) >= 1_000_000_000) return "$" + (n / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return "$" + n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toFixed(1) + "%";
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #F59E0B, #FBBF24)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function RatesProperty() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [arrearsData, setArrearsData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [sa2, setSa2] = useState("all");
  const [category, setCategory] = useState("all");

  // ── Fetch KPIs + SA2 areas ──
  const fetchBase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiRes, sa2Res] = await Promise.all([
        accessService.get(`/rates/kpis?fy=${fy}`),
        accessService.get("/abs/sa2-areas"),
      ]);
      setKpis(kpiRes.data);
      setSa2Areas(sa2Res.data);
    } catch (err: any) {
      setError(err.message || "Failed to load rates data");
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  // ── Fetch revenue data ──
  const fetchRevenue = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      if (category !== "all") params.set("category", category);
      const res = await accessService.get(`/rates/revenue?${params}`);
      setRevenueData(res.data);
    } catch (err: any) {
      console.error("Revenue error:", err);
    }
  }, [fy, sa2, category]);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  // ── Fetch arrears data ──
  const fetchArrears = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      const res = await accessService.get(`/rates/arrears?${params}`);
      setArrearsData(res.data);
    } catch (err: any) {
      console.error("Arrears error:", err);
    }
  }, [fy, sa2]);

  useEffect(() => { fetchArrears(); }, [fetchArrears]);

  // ── Fetch trend data ──
  const fetchTrend = useCallback(async () => {
    try {
      const params = sa2 !== "all" ? `?sa2=${sa2}&metric=total_civ` : "?metric=total_civ";
      const res = await accessService.get(`/rates/trend${params}`);
      setTrendData(res.data);
    } catch (err: any) {
      console.error("Trend error:", err);
    }
  }, [sa2]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  // ── Stacked bar data: revenue by suburb, stacked by category ──
  const stackedBarData = useMemo(() => {
    if (revenueData.length === 0) return [];
    const bySuburb: Record<string, any> = {};
    revenueData.forEach(row => {
      const name = row.sa2_name;
      if (!bySuburb[name]) bySuburb[name] = { sa2_name: name };
      bySuburb[name][row.category] = Number(row.rate_revenue) || 0;
    });
    return Object.values(bySuburb).sort((a: any, b: any) => {
      const totalA = (a.Residential || 0) + (a.Commercial || 0) + (a.Industrial || 0) + (a["Cultural/Rec"] || 0);
      const totalB = (b.Residential || 0) + (b.Commercial || 0) + (b.Industrial || 0) + (b["Cultural/Rec"] || 0);
      return totalB - totalA;
    });
  }, [revenueData]);

  // ── Pie data: revenue split by category ──
  const pieData = useMemo(() => {
    if (revenueData.length === 0) return [];
    const byCat: Record<string, number> = {};
    revenueData.forEach(row => {
      byCat[row.category] = (byCat[row.category] || 0) + Number(row.rate_revenue || 0);
    });
    return Object.entries(byCat).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  }, [revenueData]);

  // ── KPI cards ──
  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "Money", color: "#F59E0B", label: "Total Rate Revenue", value: fmtCurrency(kpis.total_rate_revenue), sub: `FY ${kpis.financial_year}` },
      { icon: "NumberedList", color: "#38BDF8", label: "Total Assessments", value: fmtNum(kpis.total_assessments), sub: "all categories" },
      { icon: "Completed", color: "#34D399", label: "Collection Rate", value: fmtPct(kpis.avg_collection_rate), sub: "avg across SA2s" },
      { icon: "Warning", color: "#F472B6", label: "Total Arrears", value: fmtCurrency(kpis.total_arrears), sub: `FY ${kpis.financial_year}` },
    ];
  }, [kpis]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && !kpis) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading rates & property data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#F59E0B', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Rates & Property</div>
          <div className={styles.subtitle}>
            Council rate revenue, property valuations (CIV/NAV), and rate arrears
            by SA2 area and financial year. Covers residential, commercial, industrial
            and cultural/recreational categories across {sa2Areas.length} suburbs.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><CurrencyDollar size={12} weight="regular" /> Rates & Property</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Category</div>
            <Dropdown
              selectedKey={category}
              options={CATEGORY_OPTIONS}
              onChange={(_, opt) => opt && setCategory(String(opt.key))}
              styles={{ root: { width: 200 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Stacked bar chart: revenue by suburb */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Rate Revenue by Suburb ({fy})</div>
            {stackedBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={stackedBarData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="sa2_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Residential" stackId="a" fill={CATEGORY_COLORS.Residential} />
                  <Bar dataKey="Commercial" stackId="a" fill={CATEGORY_COLORS.Commercial} />
                  <Bar dataKey="Industrial" stackId="a" fill={CATEGORY_COLORS.Industrial} />
                  <Bar dataKey="Cultural/Rec" stackId="a" fill={CATEGORY_COLORS["Cultural/Rec"]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No revenue data available
              </div>
            )}
          </div>

          {/* Pie chart: revenue by category */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Revenue by Category ({fy}) {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Areas"}
            </div>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No category data available
              </div>
            )}
          </div>
        </div>

        {/* ── CIV Trend Line Chart ── */}
        <div className={styles.chartsRow}>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Total CIV Trend {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Boroondara"}
            </div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Line type="monotone" dataKey="value" name="Total CIV" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
          <div /> {/* empty grid cell */}
        </div>

        {/* ── Arrears Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>
            Rate Arrears — FY {fy}
            {sa2 !== "all" && ` — ${sa2Options.find(o => o.key === sa2)?.text}`}
          </div>
          {arrearsData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Current</th>
                  <th className={styles.th}>1 Year</th>
                  <th className={styles.th}>2 Years</th>
                  <th className={styles.th}>3+ Years</th>
                  <th className={styles.th}>Total Arrears</th>
                  <th className={styles.th}>Collection Rate</th>
                </tr>
              </thead>
              <tbody>
                {arrearsData.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{fmtCurrency(row.current_arrears)}</td>
                    <td className={styles.td}>{fmtCurrency(row.arrears_1yr)}</td>
                    <td className={styles.td}>{fmtCurrency(row.arrears_2yr)}</td>
                    <td className={styles.td}>{fmtCurrency(row.arrears_3yr_plus)}</td>
                    <td className={styles.td} style={{ fontWeight: 600 }}>{fmtCurrency(row.total_arrears)}</td>
                    <td className={styles.td}>{fmtPct(row.collection_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No arrears data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Rates & Property Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about council rates, property valuations, arrears and revenue data across Boroondara suburbs.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
